// <purpose>The footer letter check, composed (2026-10-30): one ordered list of split pills from the
// served LetterCheck, the letter currently shown and the letter's open fill-in fields — blocking
// first, then warnings, then counts — plus the rows each pill's preview card lists. Pure, so every
// vocabulary case is pinned by a unit test rather than by a rendered pane.</purpose>
// <dependencies>types.ts (LetterCheck, the served shape). No React, no theme.</dependencies>
// <constraints>The check's covered/answered judgement never reaches this file (core does not serve
// it): a card row names the letter's NEAREST sentence, never a tick or a cross. A null check is the
// honest „für diese Mail nicht gelaufen", never „Geprüft". Words here are the pill vocabulary the
// operator picked; a new kind gets its words here and nowhere else.</constraints>
import type { LetterCheck, LetterCheckClaimKind, LetterCheckLetter, LetterSlot } from '../types';

export type CheckPillKind =
  | 'fields' | 'representation' | 'deadline' | 'identifier' | 'demand' | 'question' | 'threat'
  | 'checked' | 'notRun';

/** One row of a pill's preview card: the mail's words beside the letter's nearest sentence. */
export interface CheckCardRow {
  /** The claim this row is about (its quote is what compare mode marks in the mail). */
  claimIndex: number;
  /** Left column: the mail's words (an identifier row shows the identifier itself). */
  mail: string;
  /** The verbatim mail quote compare mode marks; equals `mail` except on identifier rows. */
  mailQuote: string;
  /** The letter's nearest sentence, or null when there is none. */
  letterQuote: string | null;
  /** A finding or a fact that stands in front of (or instead of) the nearest sentence. */
  note: string | null;
}

export interface CheckPill {
  kind: CheckPillKind;
  label: string;
  value: string;
  /** Quiet = a fact, not a finding: no card, softer value ink. */
  quiet: boolean;
  /** The rows its card lists; empty ⇒ the pill opens no card. */
  rows: CheckCardRow[];
  /** The sentence a screen reader hears (and the disabled Genehmigen's tooltip, for „Felder"). */
  ariaLabel: string;
}

/** The open fill-in fields of the letter shown: `total` slot segments, `open` still to fill. */
export interface FieldsState { open: number; total: number }

export const CHECK_WORDS = {
  fields: 'Felder',
  representation: 'Vollmacht',
  representationKnown: 'Schreibt für',
  deadline: 'Frist',
  identifier: 'Kennungen',
  demand: 'Forderung',
  question: 'Frage',
  threat: 'Androhung',
  checked: 'Geprüft',
  notRun: 'Briefprüfung',
  notRunValue: 'für diese Mail nicht gelaufen',
  checkedNothing: 'keine Frist, Forderung, Frage oder Kennung',
  checkedNothingButIdentifiers: 'keine Frist, Forderung oder Frage',
  review: 'prüfen',
  notInDraft: 'nicht im Entwurf',
  nearest: 'Nächster Satz',
  noNearest: 'Kein naher Satz im Entwurf',
  nothingToAnswer: 'Nichts zu beantworten',
  missingInForward: 'Fehlt in der Weiterleitung',
  senderUnknown: 'Absender nicht im Partnerregister: Vollmacht prüfen',
  senderKnown: 'Absender im Partnerregister',
} as const;

/** The ONE sentence for open fields (the served grammar of the bar's old note, kept as aria and tooltip). */
export const openFieldsSentence = (open: number): string =>
  `Noch ${open} ${open === 1 ? 'Feld' : 'Felder'} offen`;

const indexesOf = (check: LetterCheck, kind: LetterCheckClaimKind): number[] =>
  check.claims.map((c, i) => (c.kind === kind ? i : -1)).filter(i => i >= 0);

const pairQuote = (letter: LetterCheckLetter | undefined, claimIndex: number): string | null =>
  letter?.pairs.find(p => p.claimIndex === claimIndex)?.letterQuote ?? null;

const quoteRows = (check: LetterCheck, letter: LetterCheckLetter | undefined, idx: number[], note: (i: number) => string | null): CheckCardRow[] =>
  idx.map(i => ({
    claimIndex: i, mail: check.claims[i].quote, mailQuote: check.claims[i].quote,
    letterQuote: pairQuote(letter, i), note: note(i),
  }));

const fieldsPill = (fields: FieldsState | null): CheckPill[] => {
  if (!fields || fields.total === 0) return [];
  if (fields.open > 0) {
    return [{ kind: 'fields', label: CHECK_WORDS.fields, value: `${fields.open} offen`, quiet: false, rows: [], ariaLabel: openFieldsSentence(fields.open) }];
  }
  return [{ kind: 'fields', label: CHECK_WORDS.fields, value: `${fields.total} ausgefüllt`, quiet: true, rows: [], ariaLabel: `${CHECK_WORDS.fields}: ${fields.total} ausgefüllt` }];
};

/**
 * The footer's pills, in order: open fields (blocking) → warnings → counts → the quiet state.
 * `slot` is the letter currently shown; a check that has no entry for it reads as a letter with no
 * pairs (every card row then says it found no near sentence).
 */
export function composeCheckPills(check: LetterCheck | null | undefined, slot: LetterSlot, fields: FieldsState | null): CheckPill[] {
  const out: CheckPill[] = [...fieldsPill(fields)];
  if (!check) {
    out.push({ kind: 'notRun', label: CHECK_WORDS.notRun, value: CHECK_WORDS.notRunValue, quiet: true, rows: [], ariaLabel: `${CHECK_WORDS.notRun} ${CHECK_WORDS.notRunValue}` });
    return out;
  }
  const letter = check.letters[slot];
  const pill = (kind: CheckPillKind, label: string, value: string, rows: CheckCardRow[]): CheckPill =>
    ({ kind, label, value, quiet: false, rows, ariaLabel: `${label}: ${value}` });

  const warnings: CheckPill[] = [];
  const counts: CheckPill[] = [];

  // Vollmacht / Schreibt für — one pill, its words follow the register.
  const rep = check.representation;
  if (rep && check.claims[rep.claimIndex]) {
    const rows = quoteRows(check, letter, [rep.claimIndex], () => (rep.senderKnown ? CHECK_WORDS.senderKnown : CHECK_WORDS.senderUnknown));
    if (rep.senderKnown) counts.push(pill('representation', CHECK_WORDS.representationKnown, '1', rows));
    else warnings.push(pill('representation', CHECK_WORDS.representation, CHECK_WORDS.review, rows));
  }

  // Frist — a warning when a deadline's key token is not in the letter, else a count.
  const deadlines = indexesOf(check, 'deadline');
  if (deadlines.length) {
    const missing = new Set(letter?.deadlineMissing ?? []);
    const rows = quoteRows(check, letter, deadlines, i => (missing.has(i) ? CHECK_WORDS.notInDraft : null));
    if (deadlines.some(i => missing.has(i))) warnings.push(pill('deadline', CHECK_WORDS.deadline, CHECK_WORDS.notInDraft, rows));
    else counts.push(pill('deadline', CHECK_WORDS.deadline, String(deadlines.length), rows));
  }

  // Kennungen — only a forward that drops one; identifiers otherwise get no pill.
  const missingIds = (letter?.isForward ? letter.missingIdentifiers : []).filter(i => check.claims[i]?.kind === 'identifier');
  if (missingIds.length) {
    warnings.push(pill('identifier', CHECK_WORDS.identifier, `${missingIds.length} fehlen`, missingIds.map(i => ({
      claimIndex: i, mail: check.claims[i].value, mailQuote: check.claims[i].quote, letterQuote: null, note: CHECK_WORDS.missingInForward,
    }))));
  }

  const demands = indexesOf(check, 'demand');
  if (demands.length) counts.push(pill('demand', CHECK_WORDS.demand, String(demands.length), quoteRows(check, letter, demands, () => null)));
  const questions = indexesOf(check, 'question');
  if (questions.length) counts.push(pill('question', CHECK_WORDS.question, String(questions.length), quoteRows(check, letter, questions, () => null)));
  const threats = indexesOf(check, 'threat');
  if (threats.length) counts.push(pill('threat', CHECK_WORDS.threat, String(threats.length), quoteRows(check, letter, threats, () => CHECK_WORDS.nothingToAnswer)));
  // A known partner writing for someone is a count, last (it is context, not a task).
  const repIdx = counts.findIndex(p => p.kind === 'representation');
  if (repIdx >= 0) counts.push(...counts.splice(repIdx, 1));

  out.push(...warnings, ...counts);
  if (!warnings.length && !counts.length) {
    // The quiet state names what was searched — and never claims „keine Kennung" when the mail
    // named identifiers that simply needed no pill (a reply, or a forward that kept them all).
    const value = indexesOf(check, 'identifier').length ? CHECK_WORDS.checkedNothingButIdentifiers : CHECK_WORDS.checkedNothing;
    out.push({ kind: 'checked', label: CHECK_WORDS.checked, value, quiet: true, rows: [], ariaLabel: `${CHECK_WORDS.checked}: ${value}` });
  }
  return out;
}

/** The card's right column, as one line: a finding first, then the nearest sentence. */
export function cardRowLetterText(row: CheckCardRow): { text: string; muted: boolean } {
  const nearest = row.letterQuote ? `${CHECK_WORDS.nearest}: „${row.letterQuote}“` : null;
  if (row.note && nearest) return { text: `${row.note} · ${nearest}`, muted: false };
  if (row.note) return { text: row.note, muted: false };
  if (nearest) return { text: nearest, muted: false };
  return { text: CHECK_WORDS.noNearest, muted: true };
}

/** Which served letter slot a draft kind is on this suggestion (WIM carries two letters). */
export function letterSlotOfKind(
  kind: string | null | undefined,
  suggestion: { artifactKind?: string | null; secondary?: { artifactKind?: string | null } | null } | null | undefined,
): LetterSlot {
  if (kind && suggestion?.secondary?.artifactKind === kind && (suggestion.artifactKind ?? 'reply') !== kind) return 'secondary';
  return 'primary';
}
